import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export function useFetch(optionsOrUrl) {
  const normalizedOptions = typeof optionsOrUrl === "string" ? { url: optionsOrUrl } : optionsOrUrl;
  const { url, onSuccess } = normalizedOptions ?? {};

  const optionsRef = useRef({ url, onSuccess });
  optionsRef.current = { url, onSuccess };

  const [data, setData] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(!!url);
  const [hasFetched, setHasFetched] = useState(false);

  const refresh = useCallback(async (signal) => {
    setLoading(true);
    setError();

    const { url, onSuccess } = optionsRef.current;

    try {
      const res = await fetch(url, { signal });
      if (signal?.aborted) return;

      if (res.redirected && res.url.includes("/login")) {
        // do our login redirect
        window.location.href = res.url;
        return;
      }

      if (!res.ok) {
        let msg = "";
        try {
          const errorResponse = await res.json();
          if (signal?.aborted) return;

          msg = errorResponse.message;
        } catch {
          /* ignore parse errors */
        }

        throw new Error(`Request failed (${res.status})${msg ? `: ${msg}` : ""}`);
      }

      const data = await res.json();
      if (signal?.aborted) return;

      setData(data);
      onSuccess?.(data);
      setHasFetched(true);
    } catch (e) {
      if (e.name === "AbortError" || signal?.aborted) return;
      setError(e);
      setData();
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      setData();
      setError();
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    refresh(signal);

    return () => {
      controller.abort();
    };
  }, [url, refresh]);

  return useMemo(
    () => ({
      loading,
      error,
      data,
      hasFetched,
      refresh,
    }),
    [loading, error, data, hasFetched, refresh]
  );
}
